import React, { FC, useContext, useEffect } from 'react';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { Combobox, ComboboxInput, ComboboxPopover, ComboboxList, ComboboxOption } from '@reach/combobox';
import '@reach/combobox/styles.css';
import styled, { css } from 'styled-components';
import LocationContext from './Context';
import Button from './Button';
import { ReactComponent as IconRightArrow } from '../assets/icons/right-arrow.svg';
import { ReactComponent as IconLocation } from '../assets/icons/location.svg';
import { useGeolocation } from '../hooks';
import Filters from './Filters';
import { queryString } from '../utils/queryString';

const StyledSearchButton = styled(Button)``;
const StyledComboboxPopover = styled(ComboboxPopover)`
    z-index: 98;
`;
const StyledComboboxList = styled(ComboboxList)`
    ${({ theme }) => css`
        [data-reach-combobox-option] {
            font-family: Arial, sans-serif;
            padding: 10px;
        }
        [data-reach-combobox-option]:nth-child(odd) {
            font-family: Arial, sans-serif;
            background-color: ${theme.colors.lightGrey};
        }

        .google-logo {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            padding: 10px;
        }
    `}
`;

interface StyledSearchBoxProps {
    horz?: boolean;
}

const StyledSearchInput = styled(ComboboxInput)<StyledSearchBoxProps>`
    ${({ theme }) => css`
        height: 50px;
        width: 100%;
        border-radius: 3px;
        padding-left: 16px;
        padding-right: 45px;
        border: 2px solid #cccccc;
        color: ${theme.colors.copy};

        :focus {
            outline: 0;
            border-color: ${theme.colors.alt};
        }
    `};
`;

const StyledSearchBox = styled.div<StyledSearchBoxProps>`
    ${({ horz }) => css`
        position: relative;
        display: grid;
        grid-gap: 12px;
        flex-grow: 1;

        ${horz
            ? css`
                  grid-template-columns: 1fr;
                  grid-template-rows: 1fr 1fr 1fr;

                  @media (min-width: 768px) {
                      grid-template-rows: 1fr;
                      grid-template-columns: 1fr 1fr 1fr;
                  }
              `
            : css`
                  grid-template-columns: 1fr;
                  grid-template-rows: 1fr 1fr;
              `}
        margin: 0;
        /* margin: 24px 0 0 0; */
    `}
`;

interface StyledLocationButtonProps {
    readonly active?: boolean;
}

const StyledLocationButton = styled.button<StyledLocationButtonProps>`
    ${({ active }) => css`
        position: absolute;
        top: 5px;
        right: 4px;
        z-index: 8;
        background-color: transparent;
        padding: 8px;
        border: none;
        appearance: button;
        cursor: pointer;

        svg path {
            transition: fill 0.2s ease;
        }

        ${!active &&
        css`
            svg path {
                fill: #202020;
            }
        `}
    `}
`;
const StyledLocationButtonWrap = styled.div`
    position: relative;
`;

interface SearchBoxProps {
    searchLabel?: string;
}
export const SearchBox: FC<SearchBoxProps> = ({ searchLabel }) => {
    const {
        basic,
        layout,
        filters,
        selectedLatLng,
        setSelectedDescription,
        setSelectedLatLng,
        selectedDescription,
        searchType,
        setSearchType,
    } = useContext(LocationContext);

    const { location, getLocation } = useGeolocation(false);

    const {
        ready,
        value,
        suggestions: { status, data },
        setValue,
        clearSuggestions,
    } = usePlacesAutocomplete({
        requestOptions: {
            componentRestrictions: {
                country: ['gb','ie'],
            },
        },
        defaultValue: selectedDescription,
        debounce: 300,
    });

    useEffect(() => {
        if (location && location.longitude && location.latitude) {
            console.log('📍 User Location: ', { lat: location.latitude, lng: location.longitude });
            // setValue(CURRENT_LOCATION);
            setSelectedLatLng({ lat: location.latitude, lng: location.longitude });
        }
    }, [location, setSelectedDescription, setSelectedLatLng, setValue]);
    // if geo location we could use location pro to bias results
    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setValue(e.target.value);
    };

    const handleSelect = async (description) => {
        console.log('📍 Location: ', description);

        setValue(description, false);
        clearSuggestions();

        try {
            const results = await getGeocode({ address: description });
            const { lat, lng } = await getLatLng(results[0]);

            setSelectedDescription(description);
            setSelectedLatLng({ lat, lng });
            setSearchType('SELECTED');

            console.log('📍 Coordinates: ', { lat, lng });
        } catch (error) {
            console.log('😱 Error: ', error);
        }
    };

    const renderSuggestions = (): JSX.Element => {
        const suggestions = data.map(({ place_id, description }: any) => (
            <ComboboxOption key={place_id} value={description} />
        ));

        return (
            <>
                {suggestions}
                <li className="google-logo">
                    <img
                        src="https://developers.google.com/maps/documentation/images/powered_by_google_on_white.png"
                        alt="Powered by Google"
                    />
                </li>
            </>
        );
    };

    const handleGetUserLocation = () => {
        setSelectedDescription(undefined);
        setValue('');
        // TODO: What if geo fails
        getLocation();
        setSearchType('GEO');
    };

    const handleGotoMap = () => {
        const query: any = {};

        if (selectedLatLng?.lat) {
            query.lat = selectedLatLng.lat;
        }
        if (selectedLatLng?.lng) {
            query.lng = selectedLatLng.lng;
        }
        if (filters?.brand?.value) {
            query.brandID = filters.brand.value;
        }
        if (searchType) {
            query.searchType = searchType;
        }

        const pagePath = window.location.pathname;

        window?.dataLayer?.push({
            event: 'find_a_stockist',

                action: 'search',
                page_path: pagePath,
                filters: filters?.brand?.label,

        });

        window.location.href = `/stockists/map?${queryString(query)}`;
    };

    return (
        <>
            <StyledSearchBox horz={layout === 'horz'}>
                <Combobox onSelect={handleSelect} aria-labelledby="Your Location">
                    <StyledLocationButtonWrap>
                        <StyledLocationButton
                            onClick={handleGetUserLocation}
                            active={searchType === 'GEO'}
                            title="Use your current location"
                            aria-label="Use your current location"
                        >
                            <IconLocation />
                        </StyledLocationButton>
                        <StyledSearchInput
                            value={value}
                            onChange={handleInput}
                            disabled={!ready}
                            placeholder={searchType === 'GEO' ? 'Current Location' : 'Town, city or postcode'}
                        />
                    </StyledLocationButtonWrap>
                    <StyledComboboxPopover>
                        <StyledComboboxList>{status === 'OK' && renderSuggestions()}</StyledComboboxList>
                    </StyledComboboxPopover>
                </Combobox>

                {basic && <Filters />}
                <StyledSearchButton
                    label={searchLabel || 'Find'}
                    icon={<IconRightArrow />}
                    onClick={() => handleGotoMap()}
                />
            </StyledSearchBox>
        </>
    );
};
